/**
 * Split code into kinds of text for colouring — keywords, names, strings,
 * numbers, comments — without a highlighting library, so the colours come
 * from the theme and follow dark mode like everything else.
 *
 * Deliberately small: enough to read a query at a glance, not a parser.
 */

export type CodeTokenKind =
  | 'keyword'
  | 'function'
  | 'string'
  | 'number'
  | 'comment'
  | 'operator'
  | 'punctuation'
  | 'key'
  | 'literal'
  | 'plain';

export interface CodeToken {
  kind: CodeTokenKind;
  text: string;
}

export type CodeLanguage = 'sql' | 'json' | 'text';

const SQL_KEYWORDS = new Set(
  (
    'select from where and or not in is null like ilike between exists as on join inner left right full outer cross ' +
    'using group by order having limit offset union all distinct case when then else end with recursive insert into ' +
    'values update set delete create table view drop alter add column primary key foreign references index ' +
    'asc desc nulls first last over partition rows range unbounded preceding following current row filter within ' +
    'interval true false cast returning lateral qualify top fetch next only except intersect window'
  ).split(' '),
);

const SQL_TYPES = new Set(
  'int integer bigint smallint numeric decimal real double precision float boolean bool varchar char text date timestamp timestamptz time'.split(
    ' ',
  ),
);

/** One pass over the text, longest-meaningful-match first. */
const SQL_PATTERN =
  /(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^']|'')*')|("(?:[^"]|"")*"|`[^`]*`)|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_$]*)(\s*\()?|(<>|!=|>=|<=|::|\|\||[=<>+\-*/%])|([(),.;])|(\s+)|(.)/g;

export function tokenizeSql(code: string): CodeToken[] {
  const tokens: CodeToken[] = [];
  let m: RegExpExecArray | null;
  SQL_PATTERN.lastIndex = 0;
  while ((m = SQL_PATTERN.exec(code))) {
    const [, comment, str, quoted, num, word, call, op, punct, space, other] = m;
    if (comment) tokens.push({ kind: 'comment', text: comment });
    else if (str) tokens.push({ kind: 'string', text: str });
    else if (quoted) tokens.push({ kind: 'plain', text: quoted });
    else if (num) tokens.push({ kind: 'number', text: num });
    else if (word) {
      const lower = word.toLowerCase();
      if (SQL_KEYWORDS.has(lower) || SQL_TYPES.has(lower)) tokens.push({ kind: 'keyword', text: word });
      else if (call) tokens.push({ kind: 'function', text: word });
      else tokens.push({ kind: 'plain', text: word });
      if (call) tokens.push({ kind: 'punctuation', text: call });
    } else if (op) tokens.push({ kind: 'operator', text: op });
    else if (punct) tokens.push({ kind: 'punctuation', text: punct });
    else if (space) tokens.push({ kind: 'plain', text: space });
    else if (other) tokens.push({ kind: 'plain', text: other });
  }
  return tokens;
}

const JSON_PATTERN = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|\b(true|false|null)\b|([{}[\],:])|(\s+)|(.)/g;

export function tokenizeJson(code: string): CodeToken[] {
  const tokens: CodeToken[] = [];
  let m: RegExpExecArray | null;
  JSON_PATTERN.lastIndex = 0;
  while ((m = JSON_PATTERN.exec(code))) {
    const [, str, colon, num, literal, punct, space, other] = m;
    if (str) {
      tokens.push({ kind: colon ? 'key' : 'string', text: str });
      if (colon) tokens.push({ kind: 'punctuation', text: colon });
    } else if (num) tokens.push({ kind: 'number', text: num });
    else if (literal) tokens.push({ kind: 'literal', text: literal });
    else if (punct) tokens.push({ kind: 'punctuation', text: punct });
    else if (space) tokens.push({ kind: 'plain', text: space });
    else if (other) tokens.push({ kind: 'plain', text: other });
  }
  return tokens;
}

export function tokenizeCode(code: string, language: CodeLanguage): CodeToken[] {
  if (language === 'sql') return tokenizeSql(code);
  if (language === 'json') return tokenizeJson(code);
  return [{ kind: 'plain', text: code }];
}
